import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { publicAssetPath } from '../lib/publicAsset';
import { readSavedDeliveryLocation } from '../lib/deliveryLocation';
import WishlistIcon from './WishlistIcon';
import LocationPickerModal from './LocationPickerModal';
import LocationPinIcon from './LocationPinIcon';
import { STORE_NAVIGATION } from '../lib/siteNavigation';

const SECONDARY_NAVIGATION = STORE_NAVIGATION;

function CartButton({ cartCount, className = '' }) {
  return (
    <Link
      to="/cart"
      className={`btn btn-sm btn-outline-custom site-icon-button site-cart-button d-flex align-items-center justify-content-center ${className}`.trim()}
      title="Shopping Cart"
      aria-label={`Shopping cart${cartCount ? `, ${cartCount} item${cartCount === 1 ? '' : 's'}` : ''}`}
    >
      <span className="site-cart-icon" aria-hidden="true">🛒</span>
      {cartCount > 0 && (
        <span className="site-cart-count">
          {cartCount > 99 ? '99+' : cartCount}
        </span>
      )}
    </Link>
  );
}

function WishlistButton({ wishlistCount, className = '' }) {
  return (
    <Link
      to="/wishlist"
      className={`btn btn-sm btn-outline-custom site-icon-button site-wishlist-button d-flex align-items-center justify-content-center ${className}`.trim()}
      title="Wishlist"
      aria-label={`Wishlist${wishlistCount ? `, ${wishlistCount} saved item${wishlistCount === 1 ? '' : 's'}` : ''}`}
    >
      <span className="site-wishlist-icon" aria-hidden="true"><WishlistIcon /></span>
      {wishlistCount > 0 && <span className="site-wishlist-count">{wishlistCount > 99 ? '99+' : wishlistCount}</span>}
    </Link>
  );
}

function ThemeToggleButton({ theme, toggleTheme, className = '' }) {
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className={`site-theme-toggle site-theme-toggle--${theme} ${className}`.trim()}
      title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
      aria-label="Toggle Theme"
    >
      <span className="nextgen-theme-symbol" aria-hidden="true">
        <span className="nextgen-theme-symbol__sun">☼</span>
        <span className="nextgen-theme-symbol__moon">☾</span>
      </span>
    </button>
  );
}

function ProfileMenu({
  user,
  authLoading = false,
  userDropdownOpen,
  setUserDropdownOpen,
  unreadNotificationCount,
  unreadNotifications,
  handleNotificationClick,
  wishlistCount,
  logout,
  onOpenAddresses = () => {},
  className = '',
}) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    }

    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [setUserDropdownOpen]);

  if (authLoading) {
    return (
      <span className={`site-sign-in site-auth-loading ${className}`.trim()} role="status" aria-live="polite">
        Checking account…
      </span>
    );
  }

  if (!user) {
    return (
      <Link to="/login" className={`btn btn-sm btn-primary-custom site-sign-in ${className}`.trim()}>
        Sign in
      </Link>
    );
  }

  return (
    <div
      className={`dropdown position-relative site-profile-dropdown ${className}`.trim()}
      ref={dropdownRef}
      onPointerDown={(event) => event.stopPropagation()}
    >
      <button
        className="btn btn-sm btn-outline-custom dropdown-toggle site-profile-trigger d-flex align-items-center gap-2 px-3"
        type="button"
        onClick={() => setUserDropdownOpen((isOpen) => !isOpen)}
        aria-expanded={userDropdownOpen}
        aria-label={userDropdownOpen ? 'Close profile menu' : 'Open profile menu'}
      >
        <img
          src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
          alt="User Avatar"
          className="rounded-circle"
          style={{ width: '24px', height: '24px', objectFit: 'cover' }}
        />
        <span className="small font-weight-bold site-profile-trigger__name d-none d-sm-inline">
          {user.name || user.email.split('@')[0]}
        </span>
        <span className="site-profile-trigger__menu-glyph" aria-hidden="true">☰</span>
      </button>

      <ul
        className={`dropdown-menu dropdown-menu-end site-profile-menu shadow-sm ${userDropdownOpen ? 'show' : ''}`}
        style={{
          position: 'absolute',
          right: 0,
          top: '100%',
          marginTop: '0.65rem',
          display: userDropdownOpen ? 'block' : 'none',
        }}
      >
        {unreadNotifications.length > 0 && (
          <li className="site-profile-notifications" aria-label="Unread notifications">
            <div className="site-profile-notifications__heading">
              <span>Notifications</span>
              <span className="badge rounded-pill bg-danger">{unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}</span>
            </div>
            {unreadNotifications.slice(0, 3).map((notification) => (
              <button
                type="button"
                className="site-profile-notification"
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
              >
                <strong>{notification.title}</strong>
                <span>{notification.body}</span>
              </button>
            ))}
            {unreadNotificationCount > 3 && (
              <Link className="site-profile-notifications__more" to="/custom-orders" onClick={() => setUserDropdownOpen(false)}>
                View all updates
              </Link>
            )}
          </li>
        )}
        <li>
          <Link
            className="dropdown-item d-flex justify-content-between align-items-center"
            to="/orders"
            onClick={() => setUserDropdownOpen(false)}
          >
            <span>📦 My Orders</span>
          </Link>
        </li>
        <li>
          <Link
            className="dropdown-item d-flex justify-content-between align-items-center"
            to="/wishlist"
            onClick={() => setUserDropdownOpen(false)}
          >
            <span className="d-inline-flex align-items-center gap-2"><WishlistIcon /> Wishlist</span>
            {wishlistCount > 0 && <span className="badge rounded-pill bg-primary">{wishlistCount > 99 ? '99+' : wishlistCount}</span>}
          </Link>
        </li>
        <li>
          <Link
            className="dropdown-item d-flex justify-content-between align-items-center"
            to="/custom-orders"
            onClick={() => setUserDropdownOpen(false)}
          >
            <span>💬 Custom orders</span>
            {unreadNotificationCount > 0 && (
              <span className="badge rounded-pill bg-danger" aria-label={`${unreadNotificationCount} unread custom-order update${unreadNotificationCount === 1 ? '' : 's'}`}>
                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
              </span>
            )}
          </Link>
        </li>
        <li>
          <button
            type="button"
            className="dropdown-item d-flex align-items-center gap-2"
            onClick={() => {
              setUserDropdownOpen(false);
              onOpenAddresses();
            }}
          >
            <span className="site-profile-menu__icon"><LocationPinIcon /></span>
            <span>My Addresses</span>
          </button>
        </li>
        {['admin', 'superadmin', 'editor'].includes(user.role) && (
          <li>
            <Link
              className="dropdown-item text-primary font-weight-bold"
              to={user.role === 'editor' ? '/admin/products' : '/admin'}
              onClick={() => setUserDropdownOpen(false)}
            >
              ⚙️ Admin Panel
            </Link>
          </li>
        )}
        <li>
          <hr className="dropdown-divider" />
        </li>
        <li>
          <button
            className="dropdown-item text-danger"
            onClick={() => {
              setUserDropdownOpen(false);
              logout();
            }}
          >
            🚪 Sign Out
          </button>
        </li>
      </ul>
    </div>
  );
}

export default function Navbar() {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { user, token, logout, loading: authLoading } = useContext(AuthContext);
  const { cartItems, cartLoading } = useContext(CartContext);
  const { wishlistItems } = useWishlist();

  const [searchQuery, setSearchQuery] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const [savedDeliveryLocation, setSavedDeliveryLocation] = useState(() => readSavedDeliveryLocation());

  const navigate = useNavigate();
  const location = useLocation();

  const cartCount = cartLoading
    ? 0
    : cartItems?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 0;
  const wishlistCount = wishlistItems?.length || 0;
  const isHome = location.pathname === '/';
  const deliveryLabel = savedDeliveryLocation?.city || savedDeliveryLocation?.zip || 'Set your location';
  const isQuickLinkActive = (to) => {
    const [path, queryString] = to.split('?');
    if (location.pathname !== path) return false;
    if (!queryString) return path !== '/shop' || !location.search;
    const targetQuery = new URLSearchParams(queryString);
    const currentQuery = new URLSearchParams(location.search);
    return [...targetQuery.entries()].every(([key, value]) => currentQuery.get(key) === value);
  };

  useEffect(() => {
    let active = true;
    if (!token) {
      setUnreadNotificationCount(0);
      setUnreadNotifications([]);
      return undefined;
    }

    const loadNotifications = async () => {
      try {
        const res = await fetch('/api/notifications?unread=true', {
          headers: { 'x-auth-token': token },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (active) {
          setUnreadNotificationCount(Number(data.unreadCount || 0));
          setUnreadNotifications(data.notifications || []);
        }
      } catch (error) {
        console.error('Failed to fetch Hunar notification count:', error);
      }
    };

    loadNotifications();
    const interval = window.setInterval(loadNotifications, 60 * 1000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [location.pathname, token]);

  const handleNotificationClick = async (notification) => {
    if (token && notification.id) {
      try {
        await fetch(`/api/notifications/${notification.id}/read`, {
          method: 'PATCH',
          headers: { 'x-auth-token': token },
        });
      } catch (error) {
        console.error('Failed to mark Hunar notification read:', error);
      }
    }

    setUnreadNotifications((current) => current.filter((item) => item.id !== notification.id));
    setUnreadNotificationCount((current) => Math.max(0, current - 1));
    setUserDropdownOpen(false);
    if (notification.requestId) navigate(`/custom-orders/${notification.requestId}`);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <nav className="site-navbar">
      <div className="site-navbar__primary">
        <div className="container-fluid site-navbar__inner site-navbar__primary-inner">
          <Link
            className={`site-brand text-decoration-none${isHome ? ' is-active' : ''}`}
            to="/"
            aria-label="Hunar home"
            aria-current={isHome ? 'page' : undefined}
          >
            <img
              src={publicAssetPath('web_logo-cropped.png')}
              alt="Hunar logo"
              className="site-brand-logo"
            />
          </Link>

          <button
            type="button"
            className="site-delivery-location"
            onClick={() => setLocationPickerOpen(true)}
            aria-label={savedDeliveryLocation ? `Change delivery location, currently ${deliveryLabel}` : 'Set your delivery location'}
          >
              <span className="site-delivery-location__icon"><LocationPinIcon /></span>
            <span className="site-delivery-location__copy">
              <small>Deliver to</small>
              <strong>{deliveryLabel}</strong>
            </span>
          </button>

          <form className="site-search site-navbar-search" onSubmit={handleSearchSubmit}>
            <div className="input-group">
              <input
                type="search"
                className="form-control"
                placeholder="Search Hunar handmade items"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Search Hunar handmade items"
              />
              <button className="site-search-submit" type="submit" aria-label="Search">
                ⌕
              </button>
            </div>
          </form>

          <div className="site-navbar-actions">
            <div className="site-navbar-action-icons">
              <ThemeToggleButton theme={theme} toggleTheme={toggleTheme} />
              <CartButton cartCount={cartCount} />
              <WishlistButton wishlistCount={wishlistCount} />
            </div>
            <ProfileMenu
              user={user}
              authLoading={authLoading}
              userDropdownOpen={userDropdownOpen}
              setUserDropdownOpen={setUserDropdownOpen}
              unreadNotificationCount={unreadNotificationCount}
              unreadNotifications={unreadNotifications}
              handleNotificationClick={handleNotificationClick}
              wishlistCount={wishlistCount}
              logout={logout}
              onOpenAddresses={() => setLocationPickerOpen(true)}
            />
          </div>
        </div>
      </div>

      <div className="site-navbar__secondary">
        <div className="container-fluid site-navbar__inner site-navbar__secondary-inner">
          <nav className="site-secondary-links" aria-label="Store navigation">
            {SECONDARY_NAVIGATION.map(({ label, to }) => (
              <Link key={to} to={to} className={isQuickLinkActive(to) ? 'is-current' : undefined}>
                {label}
              </Link>
            ))}
          </nav>
          <span className="site-secondary-note">Handmade with care</span>
        </div>
      </div>

      <LocationPickerModal
        open={locationPickerOpen}
        onClose={() => setLocationPickerOpen(false)}
        savedLocation={savedDeliveryLocation}
        onSaved={setSavedDeliveryLocation}
        token={token}
      />
    </nav>
  );
}
